import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  Grid,
  Group,
  ThemeIcon,
  Table,
  Loader,
  Button,
  Badge,
} from "@mantine/core";
import {
  IconReceipt,
  IconCalendar,
  IconDownload,
  IconRefresh,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { usePerformRequest } from "../../../lib/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { DatePickerInput } from "@mantine/dates";

interface RepaymentSummary {
  totalRepayments: number;
  totalRepaidAmount: number;
  totalPrincipalRepaid: number;
  totalInterestRepaid: number;
  averageRepayment: number;
  onTimeRepayments: number;
  lateRepayments: number;
  repaymentRate: number;
}

interface RepaymentByDay {
  _id: string;
  count: number;
  totalAmount: number;
}

interface RepaymentResponse {
  summary: RepaymentSummary;
  repaymentsByDay: RepaymentByDay[];
}

export default function RepaymentReport() {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate?: number; endDate?: number }>({});

  const {
    data: reportData,
    isLoading,
    reloadData,
  } = usePerformRequest<{ data: RepaymentResponse }>({
    method: Endpoints.GetRepaymentReport(
      dateRange.startDate,
      dateRange.endDate
    ).method,
    route: Endpoints.GetRepaymentReport(
      dateRange.startDate,
      dateRange.endDate
    ).route,
  });

  useEffect(() => {
    const getTimestamp = (date: Date | null | string | unknown): number | undefined => {
      if (!date) return undefined;
      if (date instanceof Date) return date.getTime();
      if (typeof date === 'string') {
        const parsed = new Date(date);
        return isNaN(parsed.getTime()) ? undefined : parsed.getTime();
      }
      return undefined;
    };
    const start = getTimestamp(startDate);
    const end = getTimestamp(endDate);
    setDateRange({ startDate: start, endDate: end });
  }, [startDate, endDate]);

  useEffect(() => {
    reloadData();
  }, [dateRange]);

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const summary = reportData?.data?.summary;
  const byDay = reportData?.data?.repaymentsByDay || [];

  const statCards = summary
    ? [
        {
          label: "Total Repayments",
          value: summary.totalRepayments.toLocaleString(),
          color: "#699269",
        },
        {
          label: "Total Repaid",
          value: formatCurrency(summary.totalRepaidAmount),
          color: "#558B2F",
        },
        {
          label: "Principal Repaid",
          value: formatCurrency(summary.totalPrincipalRepaid),
          color: "#33691E",
        },
        {
          label: "Interest Repaid",
          value: formatCurrency(summary.totalInterestRepaid),
          color: "#1B5E20",
        },
        {
          label: "Average Repayment",
          value: formatCurrency(summary.averageRepayment),
          color: "#0D3E1F",
        },
        {
          label: "Repayment Rate",
          value: `${summary.repaymentRate.toFixed(2)}%`,
          color: "#699269",
        },
      ]
    : [];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <Group gap="md">
            <ThemeIcon size={48} radius="md" style={{ background: "#699269" }}>
              <IconReceipt size={24} color="white" />
            </ThemeIcon>
            <div>
              <Title order={2} fw={700}>
                Repayment Report
              </Title>
              <Text size="sm" c="dimmed">
                Monitor repayment performance and collection analytics
              </Text>
            </div>
          </Group>
          <Group gap="sm">
            <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => reloadData()}>
              Refresh
            </Button>
            <Button variant="light" leftSection={<IconDownload size={16} />} disabled>
              Export
            </Button>
          </Group>
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group gap="md">
            <DatePickerInput
              label="Start Date"
              placeholder="Select start date"
              value={startDate}
              onChange={(value) => setStartDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
            />
            <DatePickerInput
              label="End Date"
              placeholder="Select end date"
              value={endDate}
              onChange={(value) => setEndDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
              minDate={
                startDate
                  ? startDate instanceof Date
                    ? new Date(startDate.getTime() + 86400000)
                    : new Date(new Date(startDate as string).getTime() + 86400000)
                  : undefined
              }
            />
          </Group>
        </Paper>

        {isLoading ? (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Group justify="center">
              <Loader size="lg" />
            </Group>
          </Paper>
        ) : summary ? (
          <>
            <Grid gutter="md">
              {statCards.map((card, index) => (
                <Grid.Col key={card.label} span={{ base: 12, sm: 6, md: 4 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Paper shadow="sm" p="lg" radius="md" withBorder style={{ height: "100%" }}>
                      <Stack gap="xs">
                        <Text size="sm" c="dimmed" fw={500}>
                          {card.label}
                        </Text>
                        <Text size="xl" fw={700} style={{ color: card.color }}>
                          {card.value}
                        </Text>
                      </Stack>
                    </Paper>
                  </motion.div>
                </Grid.Col>
              ))}
            </Grid>

            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Paper shadow="sm" p="lg" radius="md" withBorder>
                  <Title order={4} mb="md">
                    Repayment Performance
                  </Title>
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        On-Time Repayments
                      </Text>
                      <Badge color="green" size="lg">
                        {summary.onTimeRepayments.toLocaleString()}
                      </Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Late Repayments
                      </Text>
                      <Badge color="red" size="lg">
                        {summary.lateRepayments.toLocaleString()}
                      </Badge>
                    </Group>
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>

            {byDay.length > 0 && (
              <Paper shadow="sm" p="lg" radius="md" withBorder>
                <Title order={4} mb="md">
                  Daily Repayments
                </Title>
                <Table.ScrollContainer minWidth={300}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Date</Table.Th>
                        <Table.Th>Count</Table.Th>
                        <Table.Th style={{ textAlign: "right" }}>Amount</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {byDay.map((item, index) => (
                        <Table.Tr key={index}>
                          <Table.Td>
                            <Text fw={500}>{new Date(item._id).toLocaleDateString()}</Text>
                          </Table.Td>
                          <Table.Td>{item.count}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text fw={600}>{formatCurrency(item.totalAmount)}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Paper>
            )}
          </>
        ) : (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Text c="dimmed" ta="center">
              No data available
            </Text>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

